import {
  Component,
  computed,
  inject,
  Input,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { ControlConfigurationService } from '../../../../services/reports/control-configuration.service';
import { DisplayService } from '../../../../services/reports/display.service';
import { ReportsService } from '../../../../services/reports/reports.service';
import { Snackbar } from '../../../../services/snackbar/snackbar.service';
import { BaseComponent } from '../../../base/base.component';
import { ButtonComponent } from '../../../core/buttons/button.component';
import { ToastComponent } from '../../../core/labels/toast.component';
import { ActionRowComponent } from '../../../core/panels/action-row.component';
import { StepperComponent } from '../../../core/stepper/stepper.component';
import { ReportEmailComponent } from '../report-email.component';
import { ReportOutputOptionsComponent } from '../report-output-options.component';
import { ReportSchedDetailsComponent } from '../report-sched-details.component';
import { FiltersService } from '../../../../services/filters/filters.service';

@Component({
  selector: 'app-report-schedule-step',
  imports: [
    ActionRowComponent,
    ButtonComponent,
    ReportOutputOptionsComponent,
    ReportSchedDetailsComponent,
    ReportEmailComponent,
    ToastComponent,
  ],
  template: `
    <div class="tw-py-8">
      <div class="tw-pb-8">
        <app-report-output-options #reportOutputOptions />
        @if (display.showScheduleSetup()) {
          <app-report-sched-details #schedDetails />
        }
        @if (display.showEmailRecipient() || display.showCustomEmail()) {
          <app-report-email #reportEmail />
        }
      </div>
    </div>

    @if (isDeliveryOnly) {
      <app-action-row>
        <app-button right1 emphasis="Low" label="Cancel" (click)="cancel()" />
        <app-button
          right2
          label="Save"
          [disabled]="invalid || saving()"
          (click)="save()"
        />
      </app-action-row>
    } @else if (stepper) {
      @if (showEditPersonalizedMessage()) {
        <div class="tw-mb-4">
          <app-toast type="Info">
            Edits to this personalized report will not affect any existing
            schedules.
          </app-toast>
        </div>
      }
      <app-action-row>
        <app-button
          right1
          emphasis="Low"
          label="Previous"
          (click)="previous()"
        />
        <app-button
          right2
          label="Save"
          [disabled]="invalid"
          [loading]="saving()"
          (click)="save()"
        />
      </app-action-row>
    }
  `,
  styles: ``,
})
export class ReportScheduleStepComponent
  extends BaseComponent
  implements OnInit
{
  @ViewChild('reportOutputOptions')
  reportOutputOptions: ReportOutputOptionsComponent;

  @ViewChild('schedDetails')
  schedDetails: ReportSchedDetailsComponent;

  @ViewChild('reportEmail')
  reportEmail: ReportEmailComponent;

  @Input()
  stepper: StepperComponent;

  @Input()
  isDeliveryOnly = false;

  protected reports = inject(ReportsService);
  protected display = inject(DisplayService);
  private filtersService = inject(FiltersService);
  private snackbar = inject(Snackbar);
  private controlConfig = inject(ControlConfigurationService);

  saving = signal(false);

  showEditPersonalizedMessage = computed(() =>
    this.reports.isEditPersonalizedMode(),
  );

  get invalid(): boolean {
    const result =
      this.reportOutputOptions?.invalid ||
      this.schedDetails?.invalid ||
      this.reportEmail?.fg.invalid;
    return !!result;
  }

  async ngOnInit(): Promise<void> {
    if (this.hasPdfOutputOption()) {
      const clientNumber =
        this.reports.selectedClient() ||
        this.reports.currentReport()?.reportInfo?.contractNumber;
      if (clientNumber) {
        await this.reports.checkClientCustomLogo(clientNumber);
      }
    }
  }

  private hasPdfOutputOption(): boolean {
    const outputFormatControl = this.controlConfig.getControlByNames([
      'OUTPUT_FORMAT',
      'OUTPUT_FORMAT_PDF_ONLY',
    ]);
    return (
      outputFormatControl?.options?.some((opt) => opt.code === 'PDF') ?? false
    );
  }

  previous() {
    this.updateChildComponents();
    this.stepper.previous();
  }

  cancel() {
    this.confirm.show({
      label: 'Cancel edit',
      message:
        'Are you sure you want to cancel this edit? Any unsaved changes will be discarded.',
      width: '35rem',
      confirm: () => {
        this.reports.clearReport();
        this.reports.clearMode();
        this.router.navigate(['reports'], {
          queryParams: { tab: 'scheduled' },
        });
      },
    });
  }

  async save() {
    if (this.isRecurringWithPastRunDate()) {
      this.confirm.show({
        label: 'Schedule recurring report',
        message:
          'You are attempting to schedule a recurring report for a date in the past. ' +
          'The report will run immediately for previous iterations until it is current. ' +
          'Press "Yes" to schedule or "No" to make changes.',
        width: '45rem',
        confirm: () => this.proceedWithSave(),
      });
    } else {
      await this.proceedWithSave();
    }
  }

  private async proceedWithSave() {
    const hasRecipients = this.reportEmail?.fg.controls.emails.length > 0;

    if (hasRecipients) {
      this.confirm.show({
        label: 'Acknowledgement',
        message: `I acknowledge email recipients are authorized to receive client
        reports from OmniInsights. I have verified the accuracy of the email recipients
        listed. Any changes to the report structure will require me to reaffirm
        the accuracy of the intended recipients.`,
        width: '60rem',
        yesLabel: 'Accept',
        noLabel: 'Decline',
        confirm: () => this.saveConfirmed(),
      });
    } else {
      await this.saveConfirmed();
    }
  }

  private isRecurringWithPastRunDate(): boolean {
    const frequency = this.schedDetails?.fg.controls.frequency.value;
    const runDate = this.schedDetails?.fg.controls.runDate.value;

    if (!frequency || !runDate) return false;

    const isRecurring = frequency !== 'P' && frequency !== 'N';
    if (!isRecurring) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const run = new Date(runDate);
    run.setHours(0, 0, 0, 0);

    return run < today;
  }

  async saveConfirmed() {
    this.saving.set(true);
    try {
      this.updateChildComponents();
      this.finalizeReport();

      const pending = this.reports.pendingFilter();
      if (pending) {
        const savedFilterId = await this.filtersService.saveFilter(
          pending.contractNumber,
          pending.filterName,
          pending.criteria,
        );
        if (!savedFilterId) {
          this.saving.set(false);
          return;
        }
        this.reports.currentReport.update((r) => ({
          ...r,
          reportInfo: {
            ...r.reportInfo!,
            quickFilterId: savedFilterId,
          },
        }));
        this.reports.pendingFilter.set(null);
      }

      const frequency =
        this.reports.currentReport()?.reportRequest?.schedule?.frequency;

      await this.reports.saveCurrentReport();
      this.reports.clearReport();
      await this.reports.getReports(true);

      if (this.reports.isEditScheduledDeliveryMode()) {
        this.router.navigate(['reports'], {
          queryParams: { tab: 'scheduled' },
        });
        this.snackbar.show('Edits saved', 'Success');
      } else if (this.reports.isSchedulePersonalizedMode()) {
        this.router.navigate(['reports'], {
          queryParams: { tab: 'scheduled' },
        });
        this.snackbar.show('New schedule created', 'Success');
      } else if (this.reports.isEditPersonalizedMode()) {
        this.router.navigate(['reports'], { queryParams: { tab: 'reports' } });
        this.snackbar.show('Edits saved', 'Success');
      } else if (frequency && frequency !== 'P') {
        this.router.navigate(['reports'], {
          queryParams: { tab: 'scheduled' },
        });
        this.snackbar.show('Report saved', 'Success');
      } else {
        this.router.navigate(['reports'], { queryParams: { tab: 'reports' } });
        this.snackbar.show('Report saved', 'Success');
      }
    } finally {
      this.saving.set(false);
    }
  }

  private updateChildComponents(): void {
    if (this.reportOutputOptions) {
      this.reportOutputOptions.updateReport();
    }
    if (this.schedDetails) {
      this.schedDetails.updateReport();
    }
    if (this.reportEmail) {
      this.reportEmail.updateReport();
    }
  }

  private finalizeReport(): void {
    this.reports.currentReport.update((report) => {
      if (report.reportRequest?.schedule) {
        report.reportRequest.schedule.priority = '42';
      }

      if (
        report.reportRequest?.reportFields &&
        report.reportRequest.reportFields.length > 0
      ) {
        report.reportRequest.reportFields =
          report.reportRequest.reportFields.filter(
            (field) =>
              field &&
              field.columnName &&
              field.columnName.trim() !== '' &&
              !field.columnName.startsWith('NULL_'),
          );
      }

      return report;
    });
  }
}
